/**
 * Admin Scoring Audit Service
 *
 * #78-C Phase 1: Typed client for the Platform-Admin-only
 * Scoring Audit Evidence endpoint:
 *
 *   GET /verascore/admin/scoring-audit/:memberId?limit=N
 *
 * Auth: session cookie (credentials: 'include') + 'Platform Admin' role gate on server.
 * Purpose: all calls tagged 'compliance_audit' per PRIV-20.3 convention.
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;
const PURPOSE_HEADER = 'x-request-purpose';
const PURPOSE_VALUE = 'compliance_audit';

// ============================================================================
// Types — mirror the server contract
// ============================================================================

export interface ScoringAuditRunInputs {
  yearly_gross_income?: number;
  minimum_payments_all_debt?: number;
  liquid_reserves_balance?: number;
  other_monthly_expenses?: number;
  money_left_over?: number;
  net_savings?: number;
  short_term_debt_balance?: number;
  short_term_debt_payments?: number;
  number_of_late_payments?: number;
  age_of_late_payments?: number;
}

export interface ScoringAuditRun {
  run_id: string;
  member_id: string;
  run_ts: string;
  environment: string;
  algorithm_version: string;
  pipeline_version: string;
  verascore: number | null;
  rating: string | null;
  status: string;
  last_month_included: string | null;
  // Fingerprints
  raw_inputs_fingerprint: string | null;
  raw_outputs_fingerprint: string | null;
  config_hash: string | null;
  // Determinism
  determinism_verdict: string | null;
  prev_run_id: string | null;
  prev_inputs_fp: string | null;
  prev_outputs_fp: string | null;
  prev_pipeline_version: string | null;
  // Data window
  data_cutoff_timestamp: string | null;
  // Factors
  factors: Record<string, unknown>[] | null;
  // Inputs (Level 1 aggregate — no raw PII)
  inputs: ScoringAuditRunInputs;
  created_at: string;
}

export interface ScoringAuditResolvedFrom {
  type: 'email' | 'auth0';
  value: string;
}

export interface ScoringAuditResponse {
  member_id: string;
  resolved_from?: ScoringAuditResolvedFrom;
  count: number;
  total_count: number;
  limit: number;
  offset: number;
  from_date?: string;
  to_date?: string;
  runs: ScoringAuditRun[];
}

// ============================================================================
// Helpers
// ============================================================================

function handleAuthRedirect(status: number) {
  if (status === 401) {
    console.warn('[adminScoringAuditService] 401 — redirecting to login');
    window.history.pushState({}, '', '?view=login');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

const FRIENDLY_ERRORS: Record<number, string> = {
  400: 'Invalid request — please check the member ID or email and try again.',
  401: 'Your session has expired. Please log in again.',
  403: 'You do not have permission to access scoring audit data.',
  404: 'No scoring data found for this member. Please verify the email or UUID is correct.',
  429: 'Too many requests — please wait a moment and try again.',
  500: 'An internal server error occurred. Please try again later or contact support.',
  502: 'The server is temporarily unavailable. Please try again in a few moments.',
  503: 'The service is temporarily unavailable. Please try again in a few moments.',
};

async function parseFailure(response: Response): Promise<Error> {
  try {
    const body = await response.json();
    const serverMsg = body?.message || body?.error;
    if (serverMsg) return new Error(serverMsg);
  } catch {
    // no parseable body — fall through to friendly message
  }
  return new Error(FRIENDLY_ERRORS[response.status] || `Unexpected error (HTTP ${response.status}). Please try again.`);
}

// ============================================================================
// API
// ============================================================================

// UUID v4 regex (case-insensitive)
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface ScoringAuditParams {
  identifier: string;
  limit?: number;
  offset?: number;
  fromDate?: string;  // ISO date YYYY-MM-DD
  toDate?: string;    // ISO date YYYY-MM-DD
}

export async function getScoringAudit(
  identifierOrParams: string | ScoringAuditParams,
  limit = 20,
): Promise<ScoringAuditResponse> {
  // Support both legacy (string, limit) and new (params object) call styles
  const params: ScoringAuditParams = typeof identifierOrParams === 'string'
    ? { identifier: identifierOrParams, limit }
    : identifierOrParams;

  const effectiveLimit = params.limit ?? limit;
  const effectiveOffset = params.offset ?? 0;

  if (import.meta.env.DEV) console.log(`📡 [adminScoringAuditService] getScoringAudit identifier=${params.identifier} limit=${effectiveLimit} offset=${effectiveOffset} from=${params.fromDate || '(none)'} to=${params.toDate || '(none)'}`);

  // Build query params
  const qp = new URLSearchParams();
  qp.set('limit', String(effectiveLimit));
  if (effectiveOffset > 0) qp.set('offset', String(effectiveOffset));
  if (params.fromDate) qp.set('from_date', params.fromDate);
  if (params.toDate) qp.set('to_date', params.toDate);

  // UUIDs go in the path; everything else (email, auth0) uses the lookup
  // query-param route to avoid Netlify router character restrictions.
  const isUuid = UUID_RE.test(params.identifier.trim());
  let url: string;
  if (isUuid) {
    url = `${API_BASE}/verascore/admin/scoring-audit/${params.identifier.trim()}?${qp.toString()}`;
  } else {
    qp.set('identifier', params.identifier.trim());
    url = `${API_BASE}/verascore/admin/scoring-audit/lookup?${qp.toString()}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'VS-Client-Id': VS_CLIENT_ID,
      'Content-Type': 'application/json',
      [PURPOSE_HEADER]: PURPOSE_VALUE,
    },
    credentials: 'include',
  });
  if (response.status === 401 || response.status === 403) {
    if (response.status === 401) handleAuthRedirect(response.status);
    throw await parseFailure(response);
  }
  if (!response.ok) {
    throw await parseFailure(response);
  }
  const json = await response.json();
  const data = json.data ?? json;
  // Backfill total_count/offset for backward compat with older API responses
  return {
    ...data,
    total_count: data.total_count ?? data.count ?? 0,
    offset: data.offset ?? 0,
  };
}

// ============================================================================
// Compare Types
// ============================================================================

export interface CompareRunSummary {
  run_id: string;
  run_ts: string;
  verascore: number | null;
  rating: string | null;
  algorithm_version: string;
  pipeline_version: string;
  raw_inputs_fingerprint: string | null;
  raw_outputs_fingerprint: string | null;
  config_hash: string | null;
  data_cutoff_timestamp: string | null;
}

export type CausalityLabel =
  | 'UNCHANGED'
  | 'NEW_DATA_INGESTION'
  | 'DATA_CORRECTION'
  | 'SYSTEM_TRANSFORMATION_CHANGE'
  | 'UNKNOWN';

export interface FieldDiff {
  field: string;
  run_a: number | string | null;
  run_b: number | string | null;
  delta: number | null;
  causality: CausalityLabel;
}

export interface CompareResponse {
  run_a: CompareRunSummary;
  run_b: CompareRunSummary;
  drift_verdict: string;
  is_output_drift: boolean;
  score_diff: { run_a: number | null; run_b: number | null; delta: number | null };
  field_diffs: FieldDiff[];
  changed_field_count: number;
  total_field_count: number;
}

export async function compareScoringRuns(
  runId1: string,
  runId2: string,
): Promise<CompareResponse> {
  if (import.meta.env.DEV) console.log(`📡 [adminScoringAuditService] compareScoringRuns ${runId1} vs ${runId2}`);
  const url = `${API_BASE}/verascore/admin/scoring-audit/compare/${runId1}/${runId2}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'VS-Client-Id': VS_CLIENT_ID,
      'Content-Type': 'application/json',
      [PURPOSE_HEADER]: PURPOSE_VALUE,
    },
    credentials: 'include',
  });
  if (response.status === 401 || response.status === 403) {
    if (response.status === 401) handleAuthRedirect(response.status);
    throw await parseFailure(response);
  }
  if (!response.ok) throw await parseFailure(response);
  const json = await response.json();
  return json.data ?? json;
}

// ============================================================================
// Evidence Pack Types
// ============================================================================

export interface EvidencePackResponse {
  _schema: string;
  generated_at: string;
  generated_by: string;
  run: {
    run_id: string;
    member_id: string;
    run_ts: string;
    environment: string;
    status: string;
  };
  scoring: {
    verascore: number | null;
    rating: string | null;
    algorithm_version: string;
    pipeline_version: string;
    last_month_included: string | null;
  };
  integrity: {
    raw_inputs_fingerprint: string | null;
    raw_outputs_fingerprint: string | null;
    config_hash: string | null;
    data_cutoff_timestamp: string | null;
    determinism_verdict: string | null;
  };
  comparison: {
    previous_run: Record<string, unknown>;
    inputs_match: boolean;
    outputs_match: boolean;
    version_match: boolean;
  } | null;
  factors: Record<string, unknown>[];
  inputs_aggregate: Record<string, unknown>;
  audit_trail: Array<{
    event_type: string;
    actor_id: string;
    actor_type: string;
    purpose: string;
    timestamp: string;
  }>;
  _notice: string;
}

export async function getEvidencePack(runId: string): Promise<EvidencePackResponse> {
  if (import.meta.env.DEV) console.log(`📡 [adminScoringAuditService] getEvidencePack runId=${runId}`);
  const url = `${API_BASE}/verascore/admin/scoring-audit/evidence-pack/${runId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'VS-Client-Id': VS_CLIENT_ID,
      'Content-Type': 'application/json',
      [PURPOSE_HEADER]: PURPOSE_VALUE,
    },
    credentials: 'include',
  });
  if (response.status === 401 || response.status === 403) {
    if (response.status === 401) handleAuthRedirect(response.status);
    throw await parseFailure(response);
  }
  if (!response.ok) throw await parseFailure(response);
  const json = await response.json();
  return json.data ?? json;
}

/**
 * Export scoring audit runs as a masked JSON blob (no raw PII).
 * Client-side only — strips any fields that shouldn't leave the UI.
 */
export function exportMaskedAuditJson(runs: ScoringAuditRun[]): string {
  const masked = runs.map((r) => ({
    run_id: r.run_id,
    run_ts: r.run_ts,
    verascore: r.verascore,
    rating: r.rating,
    algorithm_version: r.algorithm_version,
    pipeline_version: r.pipeline_version,
    determinism_verdict: r.determinism_verdict,
    raw_inputs_fingerprint: r.raw_inputs_fingerprint,
    raw_outputs_fingerprint: r.raw_outputs_fingerprint,
    config_hash: r.config_hash,
    data_cutoff_timestamp: r.data_cutoff_timestamp,
    factors: r.factors,
  }));
  return JSON.stringify({ exported_at: new Date().toISOString(), runs: masked }, null, 2);
}
